import {useMemo} from 'react';
import {Node, OnEdgesChange, OnNodesChange} from 'reactflow';

import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentMethod} from 'studies/typed/shared/ExperimentalInputs';
import {ConditionGroup} from 'studies/typed/shared/useExperimentDesignState';

import {ProcedureRunDesignFlowchartColumnNodeData} from './ProcedureRunDesignFlowchartColumnNode';
import {getNodeConnectionRoles} from './procedureRunDesignFlowchartConnectionUtils';
import {
  FlowchartEdgeSpec,
  GridLayoutOptions,
  makeFlowchartEdges,
  makeInitialNodes,
  StatusByNodeId,
} from './ProcedureRunDesignFlowchartLayoutUtils';
import {ProcedureRunDesignFlowchartNodeData} from './ProcedureRunDesignFlowchartNode';
import {useProcedureRunDesignFlowchartConnectionRoles} from './useProcedureRunDesignFlowchartConnectionRoles';
import {
  ProcedureRunDesignFlowchartLayoutEdge,
  useProcedureRunDesignFlowchartEdges,
} from './useProcedureRunDesignFlowchartEdges';
import type {MethodHighlightState} from './useProcedureRunDesignFlowchartHighlighting';
import {useProcedureRunDesignFlowchartHighlighting} from './useProcedureRunDesignFlowchartHighlighting';
import {useProcedureRunDesignFlowchartMeasuredLayout} from './useProcedureRunDesignFlowchartMeasuredLayout';
import {useProcedureRunDesignFlowchartNodes} from './useProcedureRunDesignFlowchartNodes';

export interface ProcedureRunDesignFlowchartLayoutOptions extends GridLayoutOptions {
  columnHeaderBottomGap: number;
}

export type ProcedureRunDesignFlowchartLayoutNodeData =
  | ProcedureRunDesignFlowchartNodeData
  | ProcedureRunDesignFlowchartColumnNodeData;
export type ProcedureRunDesignFlowchartLayoutNode = Node<ProcedureRunDesignFlowchartLayoutNodeData>;

export interface UseProcedureRunDesignFlowchartLayoutArgs {
  conditions: Array<ConditionGroup>;
  methods: Array<ExperimentMethod>;
  activeMethodId: string | null;
  procedureType: ProcedureType;
  layoutOptions: ProcedureRunDesignFlowchartLayoutOptions;
  methodHighlightState: MethodHighlightState;
  // When false, persisted conditions render with locked icon
  allowEditingPersistedConditions: boolean;
  statusByNodeId?: StatusByNodeId;
  // The single edge list to render (derived + drawn, already merged upstream).
  edgeSpecs: Array<FlowchartEdgeSpec>;
}

export interface UseProcedureRunDesignFlowchartLayoutResult {
  nodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  edges: Array<ProcedureRunDesignFlowchartLayoutEdge>;
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
}

/**
 * Manages the two-phase layout for ProcedureRunDesignFlowchart:
 * 1. Create hidden placeholder nodes so ReactFlow can measure their dimensions.
 * 2. Once dimensions are available, compute grid positions and unhide nodes.
 */
export function useProcedureRunDesignFlowchartLayout({
  conditions,
  methods,
  activeMethodId,
  procedureType,
  layoutOptions,
  methodHighlightState,
  allowEditingPersistedConditions,
  statusByNodeId,
  edgeSpecs,
}: UseProcedureRunDesignFlowchartLayoutArgs): UseProcedureRunDesignFlowchartLayoutResult {
  // Once a procedure (with methods) is attached, a procedure run design always has at least one
  // condition. The procedure-less empty state renders no methods, so it needs no conditions.
  assert(methods.length === 0 || conditions.length >= 1, 'Expected at least one condition');

  const {columnNodes: initialColumnNodes, rows: initialRows} = useMemo(
    () =>
      makeInitialNodes(
        conditions,
        methods,
        allowEditingPersistedConditions,
        procedureType,
        activeMethodId,
        statusByNodeId,
        edgeSpecs
      ),
    [
      conditions,
      methods,
      allowEditingPersistedConditions,
      activeMethodId,
      procedureType,
      statusByNodeId,
      edgeSpecs,
    ]
  );

  const {nodes, setNodes, onNodesChange} = useProcedureRunDesignFlowchartNodes({
    initialColumnNodes,
    initialRows,
  });

  // Roles depend only on methods and the active layer, not live edges. Recomputing here
  // keeps removability in sync with the drawing affordance.
  const roleByNodeId = useMemo(
    () => getNodeConnectionRoles(conditions, methods, [], activeMethodId).roleByNodeId,
    [conditions, methods, activeMethodId]
  );

  const initialEdges = useMemo(() => makeFlowchartEdges(edgeSpecs, roleByNodeId), [edgeSpecs, roleByNodeId]);

  const {edges, setEdges, onEdgesChange} = useProcedureRunDesignFlowchartEdges({initialEdges});

  useProcedureRunDesignFlowchartHighlighting({
    methodHighlightState,
    initialColumnNodes,
    initialRows,
    setNodes,
  });

  useProcedureRunDesignFlowchartConnectionRoles({
    conditions,
    methods,
    activeMethodId,
    edges,
    initialColumnNodes,
    initialRows,
    setNodes,
  });

  useProcedureRunDesignFlowchartMeasuredLayout({
    initialColumnNodes,
    initialRows,
    layoutOptions,
    nodes,
    setNodes,
    setEdges,
  });

  return {nodes, edges, onNodesChange, onEdgesChange};
}
